import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { formatCurrency, formatNumber, formatPercent } from "@/lib/format";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";

export default function ProductAnalysis() {
  const [jaar, setJaar] = useState("alle");
  const [jaren, setJaren] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    api.get("/dashboard/jaren").then(({ data }) => setJaren(data || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (jaar && jaar !== "alle") params.jaar = parseInt(jaar);
    api.get("/analytics/products", { params })
      .then(({ data }) => setItems(data || []))
      .finally(() => setLoading(false));
  }, [jaar]);

  const chartData = items.slice(0, 10);

  return (
    <div className="space-y-6" data-testid="products-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Producten</p>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mt-1">Productanalyse</h1>
        </div>
        <Select value={jaar} onValueChange={setJaar}>
          <SelectTrigger className="w-32 h-9" data-testid="products-jaar-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle jaren</SelectItem>
            {jaren.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </header>

      <Card className="bg-white border-slate-200 p-4 sm:p-6 rounded-lg">
        <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4">Top 10 producten op brutomarge</h2>
        <div className="h-[300px] sm:h-[360px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fill: "#64748b", fontSize: isMobile ? 10 : 12 }} />
              <YAxis
                type="category"
                dataKey="productnaam"
                width={isMobile ? 130 : 250}
                tick={{ fill: "#475569", fontSize: isMobile ? 10 : 11 }}
                tickFormatter={(v) => (isMobile && v?.length > 22 ? `${v.slice(0, 20)}…` : v)}
              />
              <Tooltip
                contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: 12 }}
                formatter={(value) => formatCurrency(value)}
              />
              <Bar dataKey="brutomarge" fill="#10b981" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="bg-white border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="products-table">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
                <th className="text-left py-3 px-4 font-semibold">Product</th>
                <th className="text-right py-3 px-4 font-semibold">Verkocht</th>
                <th className="text-right py-3 px-4 font-semibold">Orderregels</th>
                <th className="text-right py-3 px-4 font-semibold">Omzet excl.</th>
                <th className="text-right py-3 px-4 font-semibold">Brutomarge</th>
                <th className="text-right py-3 px-4 font-semibold">Marge %</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="6" className="text-center text-slate-400 py-8">Laden…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan="6" className="text-center text-slate-400 py-8">Geen productdata beschikbaar.</td></tr>
              )}
              {items.map((p) => (
                <tr key={p.productnummer || p.productnaam} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="py-2.5 px-4">
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      {p.productnaam}
                      {p.aantal_skus > 1 && (
                        <span
                          className="text-[10px] uppercase tracking-wider font-semibold bg-sky-50 text-sky-700 border border-sky-200 rounded px-1.5 py-0.5"
                          title={`Samengevoegd uit ${p.aantal_skus} SKU's`}
                          data-testid="product-merged-badge"
                        >
                          {p.aantal_skus}× SKU
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{p.productnummer}</div>
                  </td>
                  <td className="py-2.5 px-4 text-right tabular-nums">{formatNumber(p.aantal_verkocht)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-600">{formatNumber(p.aantal_orderregels)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-900">{formatCurrency(p.omzet_excl_btw)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums">
                    <span className={p.brutomarge >= 0 ? "text-emerald-700 font-medium" : "text-rose-700 font-medium"}>
                      {formatCurrency(p.brutomarge)}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-700">{formatPercent(p.brutomarge_percentage)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
